import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row, Button, Spinner, InputGroup } from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";
import moment from "moment";

import EChart from "../Charts/EChart";
import { getROASReport } from "../../api/getapi";
import { ECHART_TOOLTIP, ECHART_TOOLBOX, ECHART_COLORS, ECHART_PALETTE } from "../../Utilities/echartsTheme";
import { formatNumber, formatRawNumber } from "../../Utilities/numbers";

const RANGE_OPTIONS = [
  { value: "last-7-days", label: "Last 7 days" },
  { value: "last-30-days", label: "Last 30 days" },
  { value: "this-month", label: "This month" },
  { value: "this-year", label: "This year" },
  { value: "custom", label: "Custom" },
];

const EXPENSE_KEYS = ["ad_spend", "delivery_cost", "delivery_cost_on_returns", "packaging", "wht_cost", "net_product_cost"];
const EXPENSE_LABELS = {
  ad_spend: "Ad Spend",
  delivery_cost: "Delivery",
  delivery_cost_on_returns: "Delivery Returns",
  packaging: "Packaging",
  wht_cost: "WHT",
  net_product_cost: "Net Product"
};

const formatPKR = (n) => `Rs ${formatRawNumber(Number(n) || 0)}`;

const StatCard = ({ label, value, sub, accent = ECHART_COLORS.primary }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="py-2 px-3">
      <div className="text-muted" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div className="fw-bold" style={{ fontSize: "1.1rem", color: accent }}>
        {value}
      </div>
      {sub && <div className="text-muted" style={{ fontSize: "0.75rem" }}>{sub}</div>}
    </Card.Body>
  </Card>
);

const ROASNetSection = () => {
  const [range, setRange] = useState("this-month");
  const [dateFrom, setDateFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [revenueBasis, setRevenueBasis] = useState("delivered");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const applyRange = useCallback((r) => {
    setRange(r);
    if (r === "custom") return;
    const now = moment();
    switch (r) {
      case "last-7-days":
        setDateFrom(now.clone().subtract(6, "days").format("YYYY-MM-DD"));
        setDateTo(now.format("YYYY-MM-DD"));
        break;
      case "last-30-days":
        setDateFrom(now.clone().subtract(29, "days").format("YYYY-MM-DD"));
        setDateTo(now.format("YYYY-MM-DD"));
        break;
      case "this-month":
        setDateFrom(now.clone().startOf("month").format("YYYY-MM-DD"));
        setDateTo(now.format("YYYY-MM-DD"));
        break;
      case "this-year":
        setDateFrom(now.clone().startOf("year").format("YYYY-MM-DD"));
        setDateTo(now.format("YYYY-MM-DD"));
        break;
      default:
        break;
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getROASReport({ dateFrom, dateTo, revenueBasis });
      setData(res.data || []);
    } catch (err) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, revenueBasis]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const totals = useMemo(() => {
    const t = {
      revenue: 0, gross_revenue: 0, ad_spend: 0, delivery_cost: 0, delivery_cost_on_returns: 0,
      packaging: 0, wht_cost: 0, return_percentage: 0, product_cost: 0, net_product_cost: 0,
      total_expense: 0, net_profit: 0
    };
    data.forEach((d) => {
      t.revenue += Number(d.revenue) || 0;
      t.gross_revenue += Number(d.gross_revenue) || 0;
      t.ad_spend += Number(d.ad_spend) || 0;
      t.delivery_cost += Number(d.delivery_cost) || 0;
      t.delivery_cost_on_returns += Number(d.delivery_cost_on_returns) || 0;
      t.packaging += Number(d.packaging) || 0;
      t.wht_cost += Number(d.wht_cost) || 0;
      t.return_percentage = Math.max(t.return_percentage, Number(d.return_percentage) || 0);
      t.product_cost += Number(d.product_cost) || 0;
      t.net_product_cost += Number(d.net_product_cost) || 0;
      t.total_expense += Number(d.total_expense) || 0;
      t.net_profit += Number(d.net_profit) || 0;
    });
    t.roas = t.ad_spend > 0 ? t.revenue / t.ad_spend : null;
    t.net_margin_pct = t.revenue > 0 ? (t.net_profit / t.revenue) * 100 : null;
    return t;
  }, [data]);

  // Chart 1: Revenue (bar) vs Net Profit (line) — dual axis
  const revenueNetOption = useMemo(() => {
    if (!data.length) return {};
    const dates = data.map((d) => d.date);
    const revenues = data.map((d) => Number(d.revenue) || 0);
    const nets = data.map((d) => Number(d.net_profit) || 0);

    return {
      tooltip: {
        ...ECHART_TOOLTIP,
        trigger: "axis",
        formatter: (params) => {
          const date = moment(params[0].axisValue).format("DD MMM YYYY");
          let html = `<div style="font-weight:600;margin-bottom:4px">${date}</div>`;
          params.forEach((p) => {
            html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
              ${p.seriesName}: <strong>Rs ${formatRawNumber(p.value)}</strong>
            </div>`;
          });
          return html;
        },
      },
      legend: { top: 0, right: 50, textStyle: { fontSize: 12, color: "#64748B" }, itemWidth: 16, itemHeight: 10 },
      toolbox: ECHART_TOOLBOX,
      grid: { left: "3%", right: "4%", bottom: "15%", top: "12%", containLabel: true },
      xAxis: {
        type: "category",
        data: dates,
        axisLabel: { formatter: (v) => moment(v).format("DD MMM"), hideOverlap: true },
      },
      yAxis: [
        {
          type: "value",
          name: "Revenue",
          nameTextStyle: { color: ECHART_COLORS.primaryLight, fontSize: 11 },
          axisLabel: { formatter: (v) => formatNumber(v), color: ECHART_COLORS.primaryLight },
          splitLine: { lineStyle: { color: "#F1F5F9" } },
        },
        {
          type: "value",
          name: "Net Profit",
          nameTextStyle: { color: ECHART_COLORS.success, fontSize: 11 },
          axisLabel: { formatter: (v) => formatNumber(v), color: ECHART_COLORS.success },
          splitLine: { show: false },
        },
      ],
      dataZoom: [
        { type: "inside", start: 0, end: 100 },
        {
          type: "slider", start: 0, end: 100, height: 20, bottom: 5,
          borderColor: "#E2E8F0", fillerColor: "rgba(59,130,246,0.1)",
          handleStyle: { color: ECHART_COLORS.primaryLight }, moveHandleSize: 7,
        },
      ],
      series: [
        {
          name: "Revenue",
          type: "bar",
          yAxisIndex: 0,
          data: revenues,
          barWidth: "60%",
          itemStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: ECHART_COLORS.primaryLight },
                { offset: 1, color: "rgba(59,130,246,0.3)" },
              ] },
            borderRadius: [3, 3, 0, 0],
          },
          animationDuration: 600,
        },
        {
          name: "Net Profit",
          type: "line",
          yAxisIndex: 1,
          data: nets,
          smooth: true,
          symbol: "circle",
          symbolSize: 5,
          showSymbol: false,
          lineStyle: { width: 2.5, color: ECHART_COLORS.success },
          itemStyle: { color: ECHART_COLORS.success },
          areaStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(16,185,129,0.25)" },
                { offset: 1, color: "rgba(16,185,129,0.02)" },
              ] },
          },
          animationDuration: 800,
          animationDelay: 200,
        },
      ],
    };
  }, [data]);

  // Chart 2: Expense breakdown stacked column
  const expenseBreakdownOption = useMemo(() => {
    if (!data.length) return {};
    const dates = data.map((d) => d.date);
    const series = EXPENSE_KEYS.map((key, idx) => ({
      name: EXPENSE_LABELS[key],
      type: "bar",
      stack: "expenses",
      data: data.map((d) => Number(d[key]) || 0),
      barWidth: "60%",
      itemStyle: {
        color: ECHART_PALETTE[idx % ECHART_PALETTE.length],
        borderRadius: idx === EXPENSE_KEYS.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0],
      },
      emphasis: { focus: "series" },
      animationDuration: 600,
      animationDelay: idx * 80,
    }));

    return {
      tooltip: {
        ...ECHART_TOOLTIP,
        trigger: "axis",
        formatter: (params) => {
          const date = moment(params[0].axisValue).format("DD MMM YYYY");
          let html = `<div style="font-weight:600;margin-bottom:4px">${date}</div>`;
          let total = 0;
          params.forEach((p) => {
            total += p.value;
            html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
              ${p.seriesName}: <strong>Rs ${formatRawNumber(p.value)}</strong>
            </div>`;
          });
          html += `<div style="margin-top:4px;padding-top:4px;border-top:1px solid #E2E8F0;font-weight:600">Total: Rs ${formatRawNumber(total)}</div>`;
          return html;
        },
      },
      legend: { top: 0, right: 50, textStyle: { fontSize: 12, color: "#64748B" }, itemWidth: 16, itemHeight: 10, icon: "roundRect" },
      toolbox: ECHART_TOOLBOX,
      grid: { left: "3%", right: "4%", bottom: "15%", top: "12%", containLabel: true },
      xAxis: {
        type: "category",
        data: dates,
        axisLabel: { formatter: (v) => moment(v).format("DD MMM"), hideOverlap: true },
      },
      yAxis: {
        type: "value",
        name: "Expenses",
        nameTextStyle: { color: "#64748B", fontSize: 11 },
        axisLabel: { formatter: (v) => formatNumber(v), color: "#64748B" },
        splitLine: { lineStyle: { color: "#F1F5F9" } },
      },
      dataZoom: [
        { type: "inside", start: 0, end: 100 },
        {
          type: "slider", start: 0, end: 100, height: 20, bottom: 5,
          borderColor: "#E2E8F0", fillerColor: "rgba(59,130,246,0.1)",
          handleStyle: { color: ECHART_COLORS.primaryLight }, moveHandleSize: 7,
        },
      ],
      series,
    };
  }, [data]);

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="bg-white border-bottom pb-2">
        <Row className="align-items-center g-2">
          <Col xs="12" md="4">
            <h6 className="mb-0 fw-bold">ROAS &amp; Net Profit</h6>
            <small className="text-muted">
              {moment(dateFrom).format("DD MMM YYYY")} – {moment(dateTo).format("DD MMM YYYY")}
            </small>
          </Col>
          <Col xs="12" md="8" className="d-flex align-items-center gap-2 flex-wrap justify-content-md-end">
            <InputGroup style={{ width: "auto" }}>
              <InputGroup.Text className="bg-white" style={{ fontSize: "0.8rem" }}>Revenue</InputGroup.Text>
              <Form.Select
                size="sm"
                value={revenueBasis}
                onChange={(e) => setRevenueBasis(e.target.value)}
                style={{ width: 150 }}
              >
                <option value="delivered">Delivered only</option>
                <option value="all">All non-cancelled</option>
              </Form.Select>
            </InputGroup>
            <Form.Select
              size="sm"
              value={range}
              onChange={(e) => applyRange(e.target.value)}
              style={{ width: 140 }}
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
            {range === "custom" && (
              <>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{ width: 150 }}
                />
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{ width: 150 }}
                />
              </>
            )}
            <Button size="sm" variant="outline-secondary" onClick={fetchReport} disabled={loading}>
              <BiRefresh size={16} className={loading ? "spin" : ""} />
            </Button>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        <Row className="g-2 mb-3">
          <Col xs="6" md="2"><StatCard label="Revenue" value={formatPKR(totals.revenue)} accent={ECHART_COLORS.primary} /></Col>
          <Col xs="6" md="2"><StatCard label="Ad Spend" value={formatPKR(totals.ad_spend)} accent={ECHART_COLORS.accent} /></Col>
          <Col xs="6" md="2"><StatCard label="Total Expenses" value={formatPKR(totals.total_expense)} accent={ECHART_COLORS.rose} /></Col>
          <Col xs="6" md="2">
            <StatCard
              label="ROAS"
              value={totals.roas != null ? `${totals.roas.toFixed(2)}x` : "N/A"}
              sub={totals.roas != null ? `Rs ${formatRawNumber(totals.revenue)} / Rs ${formatRawNumber(totals.ad_spend)}` : "No ad spend"}
              accent={ECHART_COLORS.indigo}
            />
          </Col>
          <Col xs="6" md="2">
            <StatCard
              label="Net Profit"
              value={formatPKR(totals.net_profit)}
              accent={totals.net_profit >= 0 ? ECHART_COLORS.success : ECHART_COLORS.danger}
            />
          </Col>
          <Col xs="6" md="2">
            <StatCard
              label="Net Margin"
              value={totals.net_margin_pct != null ? `${totals.net_margin_pct.toFixed(1)}%` : "N/A"}
              accent={totals.net_margin_pct >= 0 ? ECHART_COLORS.emerald : ECHART_COLORS.danger}
            />
          </Col>
        </Row>

        <Row className="g-3">
          <Col xs="12" lg="6">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom py-2">
                <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>Revenue vs Net Profit (per day)</span>
              </Card.Header>
              <Card.Body style={{ height: 340 }}>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center h-100"><Spinner animation="border" /></div>
                ) : data.length ? (
                  <EChart option={revenueNetOption} />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">No data for the selected period</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xs="12" lg="6">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom py-2">
                <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>Expense Breakdown (per day)</span>
              </Card.Header>
              <Card.Body style={{ height: 340 }}>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center h-100"><Spinner animation="border" /></div>
                ) : data.length ? (
                  <EChart option={expenseBreakdownOption} />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">No data for the selected period</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ROASNetSection;
